import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type PaneWithAriaLabel, ThreePanesLayout} from '@github-ui/three-panes-layout'
import {Button, Heading} from '@primer/react'
import {useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {RepositoryLabelsInternal$key} from './__generated__/RepositoryLabelsInternal.graphql'
import {LabelCreate} from './LabelCreate'
import {LabelList} from './LabelList'
import {SearchBar} from './LabelSearchBar'
import styles from './RepositoryLabel.module.css'

type RepositoryLabelsInternalProps = {
  repository: RepositoryLabelsInternal$key
  leftPane?: PaneWithAriaLabel
}

export function RepositoryLabelsInternal({repository, leftPane}: RepositoryLabelsInternalProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryLabelsInternal on Repository
      @argumentDefinitions(
        first: {type: "Int!", defaultValue: 30}
        orderField: {type: "LabelOrderField", defaultValue: NAME}
        orderDirection: {type: "OrderDirection", defaultValue: ASC}
        skip: {type: "Int!"}
        query: {type: "String"}
      ) {
        ...LabelList
          @arguments(
            first: $first
            orderField: $orderField
            orderDirection: $orderDirection
            skip: $skip
            query: $query
          )
        ...LabelCreate
        viewerCanPush
      }
    `,
    repository,
  )

  return (
    <>
      <ThreePanesLayout
        contentAs="div"
        resizeable={false}
        leftPaneWidth="medium"
        leftPane={leftPane}
        middlePane={
          <div className={styles.middlePaneWrapper}>
            <div className={styles.header}>
              <Heading as="h1" className={styles.heading}>
                Labels
              </Heading>
              {data.viewerCanPush && (
                <Button variant="primary" onClick={() => setIsCreateDialogOpen(true)}>
                  New label
                </Button>
              )}
            </div>
            <SearchBar />
            <ErrorBoundary fallback={<div>Oops we could not load the labels</div>}>
              <LabelList repositoryRef={data} onCreateLabel={() => setIsCreateDialogOpen(true)} />
            </ErrorBoundary>
          </div>
        }
      />
      <LabelCreate repository={data} isOpen={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />
    </>
  )
}
